/**
 * The entity links a task or a work report carries, as display rows.
 *
 * Both detail pages answer the same question — "what is this attached
 * to?" — so they build the same row shape here rather than twice:
 *
 *   { key, label, value, to }
 *
 * ``value`` is what a human reads and is NEVER an id. ``to`` is a real
 * route or ``null``; a row with no destination renders as plain text,
 * because a chevron is a promise.
 *
 * Two rules run through all of it:
 *
 * **Only direct links.** A row appears for a foreign key stored on the
 * row itself. A task attached to a tenancy does not sprout that
 * tenancy's tenant, and a room never implies its unit or property — the
 * same refusal D4a built into the write path.
 *
 * **Room parents are not links.** ``room_unit_id`` / ``room_property_id``
 * describe where the room lives and exist so a picker can validate a
 * parent change. They are deliberately NOT rendered as unit or property
 * rows: doing so would show a person a link they never chose.
 */
import { getRoomDetailPath } from "../rooms/roomConstants";

/** The value shown when an id is set but its row cannot be named —
 * because it was deleted, or belongs to another organisation and is
 * invisible. Never the id itself. */
export function displayOrUnavailable(id, label, unavailableLabel) {
  if (!id) return null;
  const text = (label || "").trim();
  return text || unavailableLabel;
}

/** Liegenschaft. Clickable: `/admin/properties/:id` exists
 * (routes/AppRouter.jsx:158). */
export function propertyEntry(row) {
  const id = row?.property_id || null;
  return {
    key: "property",
    label: "Liegenschaft",
    value: displayOrUnavailable(
      id,
      row?.property_display_name,
      "Liegenschaft nicht verfügbar"
    ),
    to: id ? `/admin/properties/${id}` : null,
  };
}

/**
 * Zimmer. Clickable since D4c2 built `/admin/rooms/:roomId`; until then
 * this row was deliberately plain text, because a chevron is a promise
 * and there was nowhere to land.
 *
 * The path comes from `getRoomDetailPath` rather than a template literal
 * here, so the room map, this row and anything later all spell it once.
 */
export function roomEntry(row) {
  const id = row?.room_id || null;
  return {
    key: "room",
    label: "Zimmer",
    value: displayOrUnavailable(
      id,
      row?.room_display_name,
      "Zimmer nicht verfügbar"
    ),
    to: getRoomDetailPath(id),
  };
}

/** "Madrid · 4-Zimmer-Wohnung", either half alone, else a plain word. */
export function unitValue(row) {
  if (!row?.unit_id) return null;
  const parts = [
    (row.unit_short_id || "").trim(),
    (row.unit_title || "").trim(),
  ].filter(Boolean);
  return parts.join(" · ") || "Unit nicht verfügbar";
}

/** "FAHN-MV-2026-000015 · Madrid", either half alone, else a plain word. */
export function tenancyValue(row) {
  if (!row?.tenancy_id) return null;
  const parts = [
    (row.tenancy_reference_number || "").trim(),
    (row.tenancy_unit_short_id || "").trim(),
  ].filter(Boolean);
  return parts.join(" · ") || "Mietverhältnis nicht verfügbar";
}

/**
 * All seven links a TASK can carry, always all seven rows.
 *
 * Showing the empty ones as "Nicht verknüpft" is the point: the section
 * answers the question completely, and a missing row would read as a
 * rendering bug rather than as an absent link.
 *
 * Replaces a block that printed raw UUIDs as values and named the same
 * entities differently from the report page ("Objekt" for a unit,
 * "Eigentümer" for a landlord). Both are fixed here.
 */
export function taskLinkEntries(task) {
  const t = task || {};
  return [
    propertyEntry(t),
    {
      key: "unit",
      label: "Unit",
      value: unitValue(t),
      to: t.unit_id ? `/admin/units/${t.unit_id}` : null,
    },
    roomEntry(t),
    {
      key: "tenancy",
      label: "Mietverhältnis",
      value: tenancyValue(t),
      // No /admin/tenancies/:id route exists — tenancies live as cards
      // on the tenant page. Label only, deliberately not a link.
      to: null,
    },
    {
      key: "tenant",
      label: "Mieter",
      value: displayOrUnavailable(
        t.tenant_id,
        t.tenant_display_name,
        "Mieter nicht verfügbar"
      ),
      to: t.tenant_id ? `/admin/tenants/${t.tenant_id}` : null,
    },
    {
      key: "application",
      label: "Bewerbung",
      value: displayOrUnavailable(
        t.application_id,
        t.application_display_name,
        "Bewerbung nicht verfügbar"
      ),
      to: t.application_id ? `/admin/applications/${t.application_id}` : null,
    },
    {
      key: "landlord",
      label: "Vermieter",
      value: displayOrUnavailable(
        t.landlord_id,
        t.landlord_display_name,
        "Vermieter nicht verfügbar"
      ),
      to: t.landlord_id ? `/admin/landlords/${t.landlord_id}` : null,
    },
  ];
}
